<template>
  <div class="payment-method-list">
    <h1 class="page-title">支付方式管理</h1>

    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>支付方式列表</h2>
          <el-button type="primary" @click="showAddDialog">
            <el-icon><Plus /></el-icon>
            添加支付方式
          </el-button>
        </div>
      </template>

      <div class="card-filter">
        <el-form :inline="true" :model="searchParams" label-position="left">
          <el-form-item label="关键词搜索">
            <el-input
              v-model="searchParams.keyword"
              placeholder="请输入名称或代码"
              clearable
              @keyup.enter="fetchPaymentMethods"
            >
              <template #append>
                <el-button @click="fetchPaymentMethods">
                  <el-icon><Search /></el-icon>
                </el-button>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item label="状态">
            <el-select v-model="searchParams.status" placeholder="请选择状态" clearable>
              <el-option label="启用" :value="true" />
              <el-option label="禁用" :value="false" />
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-button type="default" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
      </div>

      <div class="card-table">
        <el-table
          v-loading="loading"
          :data="paymentMethods"
          border
          style="width: 100%"
          stripe
          size="medium"
        >
          <el-table-column prop="name" label="名称" align="center" />
          <el-table-column prop="code" label="代码" align="center" />

          <el-table-column prop="description" label="描述" align="center" show-overflow-tooltip />
          <el-table-column prop="status" label="状态" align="center" width="100">
            <template #default="scope">
              <el-tag :type="scope.row.status ? 'success' : 'danger'" size="small">
                {{ scope.row.status ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="sort_order" label="排序" align="center" width="80" />
          <el-table-column label="操作" align="center" width="180">
            <template #default="scope">
              <el-button type="primary" size="small" @click="handleEdit(scope.row)">
                <el-icon><Edit /></el-icon>
                编辑
              </el-button>
              <el-button type="danger" size="small" @click="handleDelete(scope.row)">
                <el-icon><Delete /></el-icon>
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 添加/编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      center
    >
      <el-form :model="formData" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="名称" prop="name">
          <el-input v-model="formData.name" placeholder="请输入名称" />
        </el-form-item>
        <el-form-item label="代码" prop="code">
          <el-input v-model="formData.code" placeholder="请输入代码" />
        </el-form-item>

        <el-form-item label="描述" prop="description">
          <el-input
            v-model="formData.description"
            type="textarea"
            :rows="3"
            placeholder="请输入描述"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch v-model="formData.status" />
        </el-form-item>
        <el-form-item label="排序" prop="sort_order">
          <el-input-number v-model="formData.sort_order" :min="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  fetchPaymentMethodList,
  addPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod
} from '@/api/paymentMethod';

const loading = ref(false);
const paymentMethods = ref([]);
const currentPage = ref(1);
const pageSize = ref(10);
const total = ref(0);

const searchParams = reactive({
  keyword: '',
  status: null
});

const dialogVisible = ref(false);
const dialogTitle = ref('添加支付方式');
const formRef = ref(null);
const editingId = ref(null);

const formData = reactive({
  name: '',
  code: '',
  description: '',
  status: true,
  sort_order: 0
});

const rules = {
  name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
  code: [{ required: true, message: '请输入代码', trigger: 'blur' }]
};

const fetchPaymentMethods = async () => {
  loading.value = true;
  try {
    const res = await fetchPaymentMethodList({
      page: currentPage.value,
      pageSize: pageSize.value,
      ...searchParams
    });
    paymentMethods.value = res.data;
    total.value = res.total;
  } catch (error) {
    ElMessage.error('获取支付方式列表失败');
  } finally {
    loading.value = false;
  }
};

const resetSearch = () => {
  searchParams.keyword = '';
  searchParams.status = null;
  currentPage.value = 1;
  fetchPaymentMethods();
};

const handleSizeChange = (val) => {
  pageSize.value = val;
  fetchPaymentMethods();
};

const handleCurrentChange = (val) => {
  currentPage.value = val;
  fetchPaymentMethods();
};

const showAddDialog = () => {
  dialogTitle.value = '添加支付方式';
  editingId.value = null;
  Object.assign(formData, {
    name: '',
    code: '',
    description: '',
    status: true,
    sort_order: 0
  });
  dialogVisible.value = true;
};

const handleEdit = (row) => {
  dialogTitle.value = '编辑支付方式';
  editingId.value = row.id;
  Object.assign(formData, {
    name: row.name,
    code: row.code,
    description: row.description,
    status: row.status,
    sort_order: row.sort_order
  });
  dialogVisible.value = true;
};

const handleSubmit = async () => {
  if (!formRef.value) return;

  await formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        if (editingId.value) {
          await updatePaymentMethod(editingId.value, formData);
          ElMessage.success('更新成功');
        } else {
          await addPaymentMethod(formData);
          ElMessage.success('添加成功');
        }
        dialogVisible.value = false;
        fetchPaymentMethods();
      } catch (error) {
        ElMessage.error(error.message || '操作失败');
      }
    }
  });
};

const handleDelete = (row) => {
  ElMessageBox.confirm('确定要删除该支付方式吗？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      await deletePaymentMethod(row.id);
      ElMessage.success('删除成功');
      fetchPaymentMethods();
    } catch (error) {
      ElMessage.error('删除失败');
    }
  });
};

onMounted(() => {
  fetchPaymentMethods();
});
</script>

<style scoped>
.payment-method-list {
  padding: 20px;
}

.page-title {
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: bold;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-filter {
  margin-bottom: 20px;
}

.card-footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>
